import template from "./entity-assoc.html";
import "./entity-assoc.less";
import {
	forEach,
	some
} from "lodash";
import {
	FORM_ENTITY_DETAILS, 
	FORM_ENTITY_ASSOCIATION 
} from "routes.js";
import { ENTITY_TYPES } from "constants.js";
import pluralize from "pluralize";
import { ENTITY_TRANSLATION_KEYS } from "../../../services/i18nService";
import { globs } from "../../../services/globs";
import {
	Action, 
	All 
} from "../../../services/security/actionScopes";
import { isTypeWritePermittedScope } from "../../../services/security/commonScopes";
import { isSupportedOrNullish } from "../isSupportedOrNullish";
import { getAssociationColumnVisibility } from "./entityAssocModel";

export function controller(
	appState,
	api,
	loadingService,
	entityManager,
	$uibModal,
	$logger,
	securityService,
	toastr
) {
	const ctrl = this;

	ctrl.$onInit = onInit;
	ctrl.ENTITY_TRANSLATION_KEYS = ENTITY_TRANSLATION_KEYS;

	ctrl.edit = (entity) => {
		ctrl.changeEntity(entity);
	};

	function onInit() {
		ctrl.form = appState.forms.selected;
		ctrl.availableAssociations = [];
		ctrl.associationTabs = [];

		const canViewAssociated = globs.entityTypes.byType(ctrl.typeConfig.Code).related.byCode("*").read;
		if(!securityService.isActionOrPatternAllowed(canViewAssociated)
			|| !isSupportedOrNullish(ctrl.typeConfig.Relationships)) {
			return;
		}

		if(ctrl.entity?.Type !== undefined) {
			const thisType = ctrl.form.Agency.Types.find(t => t.Code === ctrl.entity.Type.Code);
			ctrl.entity.Type.Name = thisType.Name;
		}

		const availableRelationshipTypeCodes = ctrl.typeConfig
			.Relationships
			?.Items
			?.map(r => r.Item.Code) ?? [];

		ctrl.availableAssociations = ctrl.form.Agency.Types
			// not self
			.filter(t => t.Code !== ctrl.typeConfig.Code)
			// security allowed
			.filter(t => t.Relationships?.Items?.some(r => {
				const isMatchingRelationship = availableRelationshipTypeCodes.indexOf(r.Item.Code) > -1;
				if(isMatchingRelationship) {
					const canAddSource = new Action(
						globs.entityTypes.byType(ctrl.typeConfig.Code).write
					);
					const canAddRelationshipType = new Action(
						globs.entityTypes.byType(ctrl.typeConfig.Code).related.byCode(r.Item.Code).create
					);
					const canRelateToType = new Action(
						globs.entityTypes.byType(ctrl.typeConfig.Code).related.byCode(r.Item.Code).types.byType(t.Code).add
					);
					const isAllowedRelationship = securityService.isActionScopeAllowed(
						new All([canAddSource, canAddRelationshipType, canRelateToType])
					);
					return isAllowedRelationship;
				}
			}))
			// either has no target or this is the remote's target
			.filter(t => {
				const hasDefinedTarget = t.Relationships.Items.some(i => i.Target === undefined || i.Target.Code === ctrl.typeConfig.Code);
				
				return hasDefinedTarget;
			})
			.map(entityType => {
				//find all that are a potential match
				const potentialRelationshipTypes = entityType.Relationships.Items.filter(r =>  
					(r.Target?.Code === ctrl.typeConfig.Code)
					|| (availableRelationshipTypeCodes.indexOf(r.Item.Code) > -1)
				);

				//if there is more than 1 potential match, and any of them have targeting, opt into target filtering mode, else fallback; optimizes the 0/1 case, and legacy case is maintained
				const relationships = potentialRelationshipTypes.length > 1 && potentialRelationshipTypes.filter(r => r.Target?.Code !== undefined).length > 0 
					? potentialRelationshipTypes.filter(r => r.Target?.Code === ctrl.typeConfig.Code)
					: potentialRelationshipTypes;

				if(relationships.length > 1) {
					const errorString = `Multiple relationship types exist for the pair ${ctrl.typeConfig.Code}/${entityType.Code}`;
					const err = new Error(errorString);
					$logger.exception(err, "Multiple relationships exception", relationships);

					toastr.error(errorString);
					return {};
				}
				const relationship = relationships[0];
				return {
					type: entityType,
					relationship
				};
			})
			.filter(t => t.type !== undefined);
		
		ctrl.associationTabs = ctrl.availableAssociations
			.map(a => ({
				...a,
				columns: getAssociationColumnVisibility(a.type),
				tab: { name: pluralize(a.type.Name), code: pluralize(a.type.Code) }
			}))
			.sort((a, b) => a.tab.name.localeCompare(b.tab.name));

		if(ctrl.entity !== undefined) {
			loadRelatedEntities(); 
		}
	}

	ctrl.addRelated = async(type)  => {
		await $uibModal
			.open({
				component: "entityHeaderModal",
				size: "sm",
				backdrop: "static",
				resolve: {
					typeConfig: () => type.type,
					save: () => saveNewAssociation(type)
				}
			})
			.result;
	};

	ctrl.canEditType = (typeCode) => {
		return securityService.isActionScopeAllowed(isTypeWritePermittedScope(typeCode));
	};

	function saveNewAssociation(type) {
		return async(newEntity) => {
			const newEntityType = { Code: type.type.Code };
			const relationshipType = { Code: type.relationship.Item.Code };
			const entityPayload = {
				...newEntity,
				Type: newEntityType
			};

			const payload = {
				Type: relationshipType,
				Entity: entityPayload
			};

			await loadingService.watch(ctrl.post(FORM_ENTITY_ASSOCIATION(appState.forms.selected.Id, ctrl.entity.Id), payload));
			await loadRelatedEntities();
		};
	}

	function loadRelatedEntities() {
		const promise = api.get(FORM_ENTITY_DETAILS(appState.forms.selected.Id, ctrl.entity.Id)).then((response) => {
			ctrl.associatedList = response
				.map(r => r.Entity)
				.filter(e => e.Id !== ctrl.entity.Id);

			ctrl.hasClassifications = some(ctrl.associatedList, e => !!e.Classification);

			forEach(ctrl.associatedList, e => {
				if(e.Classification) {
					e.Classification = entityManager.getClassificationById(e.Classification.Id);
				}

				e.Type.Name = ENTITY_TYPES[e.Type.Code]?.Name ?? e.Type.Name;
			});

			ctrl.entitiesByType = ctrl.associationTabs.reduce((acc, next) => {
				let thisEntityGroup = acc[next.type.Code];

				if(thisEntityGroup === undefined) {
					thisEntityGroup = {
						TypeName: next.type.Name,
						TypeCode: next.type.Code,
						TypePluralName: pluralize(next.type.Name),
						Entities: []
					};
					acc[next.type.Code] = thisEntityGroup;
				}

				const nextEntities = ctrl.associatedList.filter(e => e.Type.Code === next.type.Code);
				thisEntityGroup.Entities.push(...nextEntities);
				next.columns = getAssociationColumnVisibility(next.type, thisEntityGroup.Entities);

				return acc;
			}, {});
		});

		return loadingService.watch(promise);
	}
}

export default angular
	.module("vesl.components.entity-assoc", [])
	.component("entityAssociated", {
		controller,
		template,
		bindings: {
			entity: "<",
			changeEntity: "=",
			typeConfig: "<",
			put: "=",
			post: "="
		}
	}).name;
